import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Sidebar from './components/layout/Sidebar';
import MainContent from './components/layout/MainContent';
import Login from './components/auth/Login';
import LoadingScreen from './components/ui/LoadingScreen';
import ProfileCompletionModal from './components/ui/ProfileCompletionModal';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Create a context for mobile menu state
export const MobileContext = createContext<{
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
}>({
  isMobileMenuOpen: false,
  toggleMobileMenu: () => { },
});

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, loading, showProfileCompletion, setShowProfileCompletion } = useAuth();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <MobileContext.Provider value={{ isMobileMenuOpen, toggleMobileMenu }}>
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />
        <Route path="/*" element={
          <ProtectedRoute>
            <div className="flex min-h-screen">
              <Sidebar collapsed={isSidebarCollapsed} setCollapsed={setIsSidebarCollapsed} />
              <MainContent isSidebarCollapsed={isSidebarCollapsed} />
            </div>
          </ProtectedRoute>
        } />
      </Routes>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileCompletion}
        onClose={() => setShowProfileCompletion(false)}
        onComplete={() => setShowProfileCompletion(false)}
      />
    </MobileContext.Provider>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

// Export a hook to use the mobile context
export const useMobileMenu = () => useContext(MobileContext);

export default App; 